import React from 'react';

const HelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Meal Portal Guide</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Important Notice - Red and Italic */}
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 italic font-medium">
              IMPORTANT: If you want to update menu changes, you need to have a doctor's note and email info@wholenutritionservices.com as soon as possible.
            </p>
          </div>
          
          <div className="space-y-4 text-gray-600">
            <section>
              <h3 className="font-medium text-blue-700 mb-2">Getting Started</h3>
              <p>Welcome to the Meal Portal! This guide will help you navigate and select your meals effectively. The portal is designed to help you choose your preferred meals for your meal plan.</p>
            </section>
            
            <section>
              <h3 className="font-medium text-blue-700 mb-2">Basic Navigation</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>The <span className="font-medium">Update Info</span> button allows you to update your personal and dietary information.</li>
                <li>The <span className="font-medium">Help</span> button (which you've used to open this guide) provides assistance at any time.</li>
                <li>Use the <span className="font-medium">Back to Top</span> button (blue circle with up arrow) that appears when scrolling to quickly return to the top.</li>
              </ul>
            </section>
            
            <section>
              <h3 className="font-medium text-blue-700 mb-2">Meal Type Selection</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Use the <span className="font-medium">Breakfast</span>, <span className="font-medium">Lunch</span>, and <span className="font-medium">Dinner</span> tabs at the top to browse meals by type.</li>
                <li>Each tab shows a badge with the number of meals you've liked in that category.</li>
                <li>When viewing one meal type, you won't see meals from other types until you switch tabs.</li>
              </ul>
            </section>
            
            <section>
              <h3 className="font-medium text-blue-700 mb-2">Browsing Meals</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Meals are displayed in a grid of two columns to maximize screen space.</li>
                <li>Each meal card shows the name, calories, protein type, and prep time.</li>
                <li>Tap the <span className="font-medium">Like</span> button on any meal to add it to your favorites.</li>
                <li>Currently viewing <span className="font-medium">20</span> meals per page.</li>
              </ul>
            </section>
            
            <section>
              <h3 className="font-medium text-blue-700 mb-2">Using Filters</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Tap the <span className="font-medium">Filters</span> button to open the filter panel.</li>
                <li><span className="font-medium">Search</span>: Use the search bar to find meals by name.</li>
                <li><span className="font-medium">Protein Type</span>: Filter meals by selecting protein options like Chicken, Beef, etc.</li>
                <li><span className="font-medium">Prep Time</span>: Set a maximum preparation time using the slider or quick buttons.</li>
                <li><span className="font-medium">Menu Types</span>: Choose between Classic, Popular, and Seasonal menus.</li>
              </ul>
            </section>
            
            <section>
              <h3 className="font-medium text-blue-700 mb-2">Meal Card Indicators</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li><span className="font-medium">S/S</span>: Spring/Summer seasonal meal</li>
                <li><span className="font-medium">F/W</span>: Fall/Winter seasonal meal</li>
                <li><span className="font-medium">P</span>: Popular/Essential menu item (always available)</li>
                <li><span className="font-medium">C</span>: Classic menu item (available year-round)</li>
                <li>Each meal also shows calorie count, protein type, sodium, and carb content.</li>
              </ul>
            </section>
            
            <section>
              <h3 className="font-medium text-blue-700 mb-2">Pagination</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Use the <span className="font-medium">Previous</span> and <span className="font-medium">Next</span> buttons at the bottom to navigate between pages.</li>
                <li>The current page and total pages are displayed (e.g., "Page 1 of 3").</li>
                <li>If there's only one page of results, pagination controls won't appear.</li>
              </ul>
            </section>
            
            <section>
              <h3 className="font-medium text-blue-700 mb-2">Managing Favorites</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Tap the <span className="font-medium">Favorites</span> button to view all your liked meals.</li>
                <li>The favorites tab shows a pink background to distinguish it from regular browsing.</li>
                <li>In the Favorites view, you can filter by meal type (All, Breakfast, Lunch, Dinner).</li>
                <li>To remove a meal from favorites, tap the <span className="font-medium">Remove</span> button.</li>
                <li>You need to select a minimum of:</li>
                <ul className="list-circle pl-5">
                  <li>8 breakfast meals</li>
                  <li>8 lunch meals</li>
                  <li>14 dinner meals</li>
                </ul>
                <li>The progress bars show how many meals you've selected in each category.</li>
                <li>When viewing favorites, use the <span className="font-medium">Back to Meal Selection</span> button to return to browsing.</li>
              </ul>
            </section>
            
            <section>
              <h3 className="font-medium text-blue-700 mb-2">Submitting Your Selection</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your meal selections are automatically saved as you make them.</li>
                <li>Once you've selected enough meals in each category, use the <span className="font-medium">Submit</span> button in the Favorites view.</li>
                <li>If you haven't selected enough meals, you'll be prompted to complete your selection.</li>
                <li>After submission, your meal plan will be processed for delivery.</li>
              </ul>
            </section>
            
            <section>
              <h3 className="font-medium text-blue-700 mb-2">Special Dietary Requirements</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>The meal options are filtered based on your dietary restrictions from your profile.</li>
                <li>For any special dietary requirements not in your profile, please contact support.</li>
                <li>Remember: Any menu changes require a doctor's note (see notice at top).</li>
              </ul>
            </section>
            
            <div className="pt-2 border-t border-gray-200 mt-4">
              <p className="text-sm text-gray-500">If you need further assistance, please contact info@wholenutritionservices.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpModal; 